import { useCallback, useEffect, useMemo, useRef } from "react";
import SkorList2 from "../../pages/tenant-profile/skorList2";
import "./ReportTenantProfiles.css";

const getId = (value) => (value?._id ?? value)?.toString?.();

// Builds the selection entries from shared profiles when no selection model is
// available: every accepted participant (or the main tenant) and all of their
// guarantors.
const buildEntriesFromSharedProfiles = (sharedProfiles = []) =>
  sharedProfiles
    .filter((sp) => sp?._id)
    .map((sp) => {
      const participants = sp?.application_id?.participants || [];
      if (participants.length > 0) {
        return {
          sharedProfileId: sp._id,
          type: "application",
          participants: participants
            .filter((p) => p?.consent_status === "accepted" && p?.tenant_info)
            .map((p) => ({
              tenantId: getId(p.tenant_info),
              guarantorIds: (p.tenant_info?.gurantor_id || []).map(getId),
            })),
        };
      }
      return {
        sharedProfileId: sp._id,
        type: "tenant",
        tenantId: getId(sp?.tenantid),
        guarantorIds: (sp?.tenantid?.gurantor_id || []).map(getId),
      };
    });

// Same profile cards the combined report shows (ReportWrapper), one SkorList2
// per selected tenant and per selected guarantor. Each card loads its own data
// and shows its own loader, so the report above it is never blocked.
const ReportTenantProfiles = ({
  selectionModel,
  sharedProfiles = [],
  sharedProfileIds = [],
  hidden = false,
  onReportDataChange,
}) => {
  // One Apply can save several people; regenerate the report once
  const changeTimerRef = useRef(null);
  const onChangeRef = useRef(onReportDataChange);
  onChangeRef.current = onReportDataChange;
  useEffect(() => () => clearTimeout(changeTimerRef.current), []);
  const handleReportDataChange = useCallback(() => {
    clearTimeout(changeTimerRef.current);
    changeTimerRef.current = setTimeout(() => onChangeRef.current?.(), 500);
  }, []);

  const cards = useMemo(() => {
    let entries = Array.isArray(selectionModel) ? selectionModel : [];
    if (!entries.length)
      entries = buildEntriesFromSharedProfiles(sharedProfiles);
    if (!entries.length) {
      entries = sharedProfileIds
        .filter(Boolean)
        .map((id) => ({ sharedProfileId: id, type: "tenant" }));
    }

    const list = [];
    entries.forEach((entry) => {
      const sharedprofileid = entry?.sharedProfileId;
      if (!sharedprofileid) return;
      const isApplication = entry.type === "application";
      const people = isApplication
        ? entry.participants || []
        : [{ tenantId: entry.tenantId, guarantorIds: entry.guarantorIds }];

      people.forEach((person) => {
        const guarantorIds = (person?.guarantorIds || []).filter(Boolean);
        const participantTenantId = isApplication
          ? person?.tenantId
          : undefined;
        const mainTenantId =
          !isApplication && person?.tenantId
            ? { _id: person.tenantId }
            : undefined;
        const baseKey = `${sharedprofileid}-${person?.tenantId ?? "main"}`;

        list.push({
          key: baseKey,
          sharedprofileid,
          participantTenantId,
          // undefined keeps SkorList2's default (all guarantors)
          reportGuarantorIds: person?.tenantId
            ? guarantorIds.join(",")
            : undefined,
        });
        guarantorIds.forEach((guarantorId) => {
          list.push({
            key: `${baseKey}-${guarantorId}`,
            sharedprofileid,
            participantTenantId,
            mainTenantId,
            guarantor: guarantorId,
          });
        });
      });
    });
    return list;
  }, [selectionModel, sharedProfiles, sharedProfileIds]);

  if (!cards.length) return null;

  return (
    // Stay mounted while hidden so the profiles keep loading
    <div className="report-tenant-profiles" hidden={hidden}>
      {cards.map(({ key, ...cardProps }) => (
        <div className="pt-0" key={key}>
          <div className="d-flex justify-content-center pb-3 pt-4">
            <img
              src="/images/SkorWhite.svg"
              alt=""
              className="report-tenant-profiles__logo"
            />
          </div>
          <SkorList2
            {...cardProps}
            showReportIcons={false}
            onReportDataChange={handleReportDataChange}
          />
        </div>
      ))}
    </div>
  );
};

export default ReportTenantProfiles;
